import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Typography,
  Button,
  Container,
} from '@mui/material';
import { Error as ErrorIcon, ArrowBack } from '@mui/icons-material';
import axios from 'axios';

// Import all themes
import {
  ModernTheme,
  ClassicTheme,
  VibrantTheme,
  MinimalTheme,
  ProfessionalTheme,
} from '../components/landing-themes';

// Theme component mapping
const themeComponents = {
  modern: ModernTheme,
  classic: ClassicTheme,
  vibrant: VibrantTheme,
  minimal: MinimalTheme,
  professional: ProfessionalTheme,
};

// API base URL - use the same base as the main app (already includes /api)
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.bookmylibrary.in/api';
const UPLOADS_BASE = API_BASE.replace('/api', '');

function LibraryLandingPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [library, setLibrary] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchLibraryData();
  }, [slug]);

  const fetchLibraryData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch library data by slug (public endpoint)
      const response = await axios.get(`${API_BASE}/libraries/by-slug/${slug}`);

      if (!response.data.error) {
        const libraryData = response.data.data.library;
        // Prepend backend base URL to gallery image paths
        if (libraryData.gallery?.length > 0) {
          libraryData.gallery = libraryData.gallery.map(img =>
            img.startsWith('/uploads/') ? `${UPLOADS_BASE}${img}` : img
          );
        }
        setLibrary(libraryData);
        setPlans(libraryData.plans || []);
      } else {
        setError('Library not found');
      }
    } catch (err) {
      console.error('Error fetching library:', err);
      if (err.response?.status === 404) {
        setError('This library page is not available.');
      } else {
        setError('Failed to load library information. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle booking - redirect to the full BML application
  const handleBookNow = () => {
    if (library) {
      // Redirect to the main BML application with the library detail page
      window.location.href = `https://bookmylibrary.in/libraries/${library.id}`;
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading library information...</Typography>
      </Box>
    );
  }

  // Error state
  if (error || !library) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom textAlign="center">
          {error || 'Library not found'}
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ mb: 3, maxWidth: 400 }}>
          The library page you're looking for might not be available or has been disabled.
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/libraries')}
          sx={{ bgcolor: '#F26522', '&:hover': { bgcolor: '#D9551A' } }}
        >
          Browse All Libraries
        </Button>
      </Box>
    );
  }

  // Get the theme component based on library settings
  const themeName = library.website_theme || 'modern';
  const ThemeComponent = themeComponents[themeName] || ModernTheme;
  const accentColor = library.website_accent_color || '#F26522';

  // Render the selected theme
  return (
    <ThemeComponent
      library={library}
      plans={plans}
      accentColor={accentColor}
      onBookNow={handleBookNow}
    />
  );
}

export default LibraryLandingPage;
